import moment from 'moment'
import { useRouter } from 'next/router'
import { useRef, useState } from 'react'

import { actionTypes, useServiceScheduleContext } from '../../context/serviceScheduleContext'

import { assetPrefix } from 'config/env_config'
import Button from 'src/components/button'
import EditableInfo from 'src/components/editableInfo'
import ChevronDown from 'static/icons/chevron-down.svg'
import ChevronUp from 'static/icons/chevron-up.svg'
import { formatPrice, getPetPhotoByType, getServiceIconByName, splitServiceName } from 'utils'
import * as S from './styles'

const SchedulingReview = ({ schedule, current, previousState }) => {
  const elementRef = useRef()
  const router = useRouter()
  const [expanded, setExpanded] = useState(!!current)
  const {
    state: schedulingState,
    dispatch,
    steps,
    functions,
    editSteps
  } = useServiceScheduleContext()

  const stateToRender = previousState?.data || schedulingState

  const {
    pricesInfo,
    handleEditMultipleScheduling
  } = functions

  const {
    serviceData,
    petsData,
    additionalServiceData,
    partnersData,
    professionalData,
    formList,
    schedulingItemError,
    invertStep,
    obs
  } = stateToRender

  const {
    currentSchedulePrice,
    currentPriceWithDiscount,
    currentHasDiscount
  } = pricesInfo()

  const schedulingHasDiscount = current ? currentHasDiscount : previousState?.scheduling?.discountedPrice < previousState?.scheduling?.price
  const schedulingPrice = current ? currentSchedulePrice : previousState?.scheduling?.price
  const schedulingPriceWithDiscount = current ? currentPriceWithDiscount : previousState?.scheduling?.discountedPrice
  const professionalList = professionalData || []
  const selectedPet = petsData.find(pet => pet.id === schedule?.pet_id)
  const selectedService = serviceData.find(service => service.value === schedule?.service_id)
  const selectedAdditionalServices = additionalServiceData.filter(additionalService => schedule?.additional_services?.includes(additionalService.value))
  const selectedPartner = partnersData?.find(partner => partner.id === schedule?.partner_id)
  const selectedProfessional = professionalList?.find(professional => professional.id === schedule?.professional_id)
  const selectedDay = schedule?.selectDay
  const selectedTime = schedule?.selectTime

  const currentSchedulingInfo = [
    {
      infoTitle: splitServiceName(selectedService?.name),
      infoIcon: getServiceIconByName(selectedService?.name, 'outline'),
      infoAdditional: (selectedAdditionalServices?.length ? '+ ' : '') + selectedAdditionalServices.map(additionalService => additionalService.name).join(', '),
      stepName: 'Serviço',
      step: 1
    },
    {
      infoTitle: selectedPartner?.name,
      infoIcon: `${assetPrefix}/static/icons/location-disabled.svg`,
      infoAdditional: selectedPartner?.address,
      stepName: 'Localização',
      step: 2
    },
    {
      infoTitle: selectedProfessional?.name?.split('CNPJ')?.[0],
      infoIcon: `${assetPrefix}/static/icons/professional-disabled.svg`,
      stepName: 'Profissional',
      step: invertStep ? 5 : 4
    },
    {
      infoTitle: selectedDay && moment(selectedDay).format('DD/MM/YYYY'),
      infoIcon: `${assetPrefix}/static/icons/datetime-disabled.svg`,
      infoAdditional: selectedTime,
      stepName: 'Data e Hora',
      step: invertStep ? 4 : 5
    },
    {
      infoTitle: obs ? 'Observações' : '',
      infoAdditional: obs,
      infoIcon: `${assetPrefix}/static/icons/comment.svg`,
      stepName: 'Serviço',
      step: 1
    }
  ]

  const onGoTo = path => {
    router.push({
      pathname: `/agendamentos/${path}`,
      query: router.query
    })
  }

  const editScheduling = (step = 0) => {
    const redirectedStep = steps.find(schedulingStep => schedulingStep.step === step)

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...editSteps[redirectedStep?.id || 0],
        formList,
        review: false,
        isHowToPay: false
      }
    }, () => {
      const stepUrl = steps.find(schedulingStep => schedulingStep.step === (step || 1))

      onGoTo(stepUrl.url)
    })
  }

  const handleExpand = () => {
    setExpanded(!expanded)
  }

  const Chevron = {
    true: <ChevronUp />,
    false: <ChevronDown />
  }

  return (
    <S.StickyCard
      onClick={() => {
        if (!current) handleExpand()
      }} expanded={expanded}
    >
      <S.Container ref={elementRef}>
        <S.InfoCard
          active
          marginBottom={0}
          onClick={() => {
            if (current) editScheduling()
          }}
          expanded={expanded}
        >
          <S.InfoCardContent expanded={expanded}>
            <S.Photo
              src={selectedPet?.photo || getPetPhotoByType(selectedPet?.breed?.type)}
              alt={selectedPet?.name}
              setDefaultPhoto={!selectedPet?.photo}
            />
            <S.Summary>
              <S.Pet title={selectedPet?.name}>
                <p className='pet-name'>{selectedPet?.name}</p>
                <p className='pet-breed-name'>{selectedPet?.breed?.name}</p>
                {!!schedulingItemError && (
                  <S.WarningStatus>
                    <img src={`${assetPrefix}/static/icons/warning-status.svg`} alt='Erro' />
                  </S.WarningStatus>
                )}
              </S.Pet>

            </S.Summary>
            {
              current ? <S.Edit src={`${assetPrefix}/static/icons/pen-outline.svg`} alt='Editar ítem' /> : Chevron[expanded]
            }
          </S.InfoCardContent>
        </S.InfoCard>
        <S.ExpandedInfo expanded={expanded}>
          <h2>Informações principais</h2>
          <S.CurrentSchedule expanded={expanded}>
            {currentSchedulingInfo.sort((a, b) => a.infoTitle === 'Observações' ? a : a.step - b.step).map((schedule, i) => {
              if (schedule.infoTitle) {
                return (
                  <EditableInfo
                    noEdit={!current}
                    key={i}
                    icon={schedule.infoIcon}
                    iconAlt={schedule.stepName}
                    iconSize={24}
                    title={schedule.infoTitle}
                    additionalInfo={schedule.infoAdditional}
                    onEdit={() => editScheduling(schedule.step)}
                    isObs={schedule.infoTitle === 'Observações'}
                  />
                )
              }
            })}
          </S.CurrentSchedule>
          <S.WrapperPricesAndAnotherSchedulings>
            <S.CalcPrice expanded={expanded}>
              <img src={`${assetPrefix}/static/icons/money-outline.svg`} />
              <div>{(formList.length || !current) ? 'Subtotal' : 'Total'}</div>
              <S.Total>
                <>
                  {schedulingHasDiscount && <S.Total previousPrice>{formatPrice(schedulingPrice)}</S.Total>}
                  {schedulingHasDiscount ? formatPrice(schedulingPriceWithDiscount) : formatPrice(schedulingPrice)}
                </>
              </S.Total>
            </S.CalcPrice>
            {!current && <Button onClick={() => handleEditMultipleScheduling(previousState)} margin='16px 0 0 0' theme='secondary' data-test='edit-scheduling'>Editar agendamento</Button>}
          </S.WrapperPricesAndAnotherSchedulings>
        </S.ExpandedInfo>
      </S.Container>
    </S.StickyCard>
  )
}

export default SchedulingReview
